import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Grid, Paper, Typography, Box, Chip, Alert, CircularProgress
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SwapHorizIcon      from "@mui/icons-material/SwapHoriz";
import CloseIcon          from "@mui/icons-material/Close";
import CurrencyRupeeIcon  from "@mui/icons-material/CurrencyRupee";
import LocationOnIcon     from "@mui/icons-material/LocationOn";
import TwoWheelerIcon     from "@mui/icons-material/TwoWheeler";
import PersonIcon         from "@mui/icons-material/Person";
import PaymentIcon        from "@mui/icons-material/Payment";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";

const Label = ({ children, color = "#555" }) => (
  <Typography variant="caption" sx={{ color, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.65rem" }}>
    {children}
  </Typography>
);

const Val = ({ children, color }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, color: color || "#111", mt: 0.1 }}>
    {children || "—"}
  </Typography>
);

const InfoBlock = ({ label, value, color }) => (
  <Box>
    <Label>{label}</Label>
    <Val color={color}>{value}</Val>
  </Box>
);

// ₹ formatter
const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

const BikeDetailModal = ({ open, onClose, selectedBike, detailLoading }) => {
  // 🎁 GIFTS — array of gifts.
  // If the data has priced items (from the profit/loss API: accessoryDetail.gift.items)
  // we use those (name, qty, price, cost). Otherwise we fall back to the plain
  // sale.gifts array ([{ name, quantity }]) which has no price.
  const pricedGiftItems = selectedBike?.accessoryDetail?.gift?.items;
  const giftItems = Array.isArray(pricedGiftItems) && pricedGiftItems.length > 0
    ? pricedGiftItems.map((g) => ({
        name:  g.name,
        qty:   g.qty,
        price: g.price,
        cost:  g.cost,
      }))
    : (Array.isArray(selectedBike?.gifts) ? selectedBike.gifts : []).map((g) => ({
        name:  g.name,
        qty:   g.quantity,
        price: undefined,
        cost:  undefined,
      }));

  // 🎁 TOTAL gift price — prefer the API total, else sum item costs (only when priced)
  const hasGiftPricing = giftItems.length > 0 && giftItems.every((g) => g.cost != null);
  const giftTotalPrice =
    selectedBike?.accessoryDetail?.gift?.cost != null
      ? selectedBike.accessoryDetail.gift.cost
      : hasGiftPricing
        ? giftItems.reduce((sum, g) => sum + (Number(g.cost) || 0), 0)
        : null;

  const giftTotalQty = giftItems.reduce((sum, g) => sum + (Number(g.qty) || 0), 0);

   return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, bgcolor: "#0d1b2a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        🧾 Sale Details — {selectedBike?.invoiceNo}
        <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#f8fafc", p: 2.5 }}>
        {detailLoading ? (
          <Box textAlign="center" py={5}><CircularProgress /></Box>
        ) : selectedBike ? (
          <Grid container spacing={2}>

            {/* ── BIKE INFO ── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                <Typography fontWeight={800} mb={1.5} color="#1565c0" display="flex" alignItems="center" gap={0.5}>
                  <TwoWheelerIcon fontSize="small" /> Bike Info
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}><InfoBlock label="Model"    value={selectedBike?.model} /></Grid>
                  <Grid item xs={6}><InfoBlock label="SKU"      value={selectedBike?.skuCode} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Color"    value={selectedBike?.color} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Key No"   value={selectedBike?.keyNumber} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Engine"  value={selectedBike?.engineNumber} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Chasis"  value={selectedBike?.chasisNumber} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Location" value={selectedBike?.location} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Invoice"  value={selectedBike?.invoiceNo} color="#1565c0" /></Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ── CUSTOMER ── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                <Typography fontWeight={800} mb={1.5} color="#2e7d32" display="flex" alignItems="center" gap={0.5}>
                  <PersonIcon fontSize="small" /> Customer
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}><InfoBlock label="Name"           value={selectedBike?.customerName} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Father / C/O"   value={selectedBike?.fatherName} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Mobile"         value={selectedBike?.mobile?.join(", ")} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Address"        value={selectedBike?.address} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="Village"        value={selectedBike?.village} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="Post Office"    value={selectedBike?.postOffice} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="Police Station" value={selectedBike?.policeStation} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="PIN Code"       value={selectedBike?.pinCode} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Landmark"       value={selectedBike?.landmark} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="GSTIN"       value={selectedBike?.GSTIN} /></Grid>
                  <Grid item xs={6}> <InfoBlock label="Reg. No"       value={selectedBike?.regNo} /></Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ── SALE DETAILS ── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                <Typography fontWeight={800} mb={1.5} color="#7b1fa2" display="flex" alignItems="center" gap={0.5}>
                  <CurrencyRupeeIcon fontSize="small" /> Sale Details
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}><InfoBlock label="ExShowroom Price" value={`₹${(selectedBike?.salePrice || 0).toLocaleString("en-IN")}`} /></Grid>
                  <Grid item xs={6}><InfoBlock label="ORP Price" value={`₹${(selectedBike?.orp || 0).toLocaleString("en-IN")}`} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Sale Date"  value={selectedBike?.saleDate ? new Date(selectedBike.saleDate).toLocaleDateString("en-IN") : "—"} /></Grid>
                  <Grid item xs={6}>
                    <Label>Sale Type</Label>
                    <Box mt={0.3}>
                      <Chip
                        label={selectedBike?.saleType || "—"} size="small"
                        sx={{
                          fontWeight: 700, fontSize: "0.72rem",
                          bgcolor:
                            selectedBike?.saleType === "CASH"     ? "#e8f5e9" :
                            selectedBike?.saleType === "FINANCE"  ? "#e3f2fd" :
                            selectedBike?.saleType === "EXCHANGE" ? "#fff3e0" : "#f5f5f5",
                          color:
                            selectedBike?.saleType === "CASH"     ? "#2e7d32" :
                            selectedBike?.saleType === "FINANCE"  ? "#1565c0" :
                            selectedBike?.saleType === "EXCHANGE" ? "#e65100" : "#555"
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}><InfoBlock label="Discount"   value={`₹${(selectedBike?.discount || 0).toLocaleString("en-IN")}`} color="#c62828" /></Grid>
                  <Grid item xs={6}><InfoBlock label="Cash Paid"  value={`₹${(selectedBike?.cashAmount || 0).toLocaleString("en-IN")}`} color="#2e7d32" /></Grid>
                  <Grid item xs={6}><InfoBlock label="DP Paid"  value={`₹${(selectedBike?.DP || 0).toLocaleString("en-IN")}`} color="#2e7d32" /></Grid>
                  <Grid item xs={6}>
                    <Label>Net Amount</Label>
                    <Val color="#7b1fa2">
                      ₹{((selectedBike?.salePrice || 0) - (selectedBike?.discount || 0)).toLocaleString("en-IN")}
                    </Val>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ── PAYMENT STATUS / DUE ── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2,
                border: `1px solid ${selectedBike?.due?.status === "DUE" ? "#ffb74d" : "#a5d6a7"}`,
                bgcolor: selectedBike?.due?.status === "DUE" ? "#fff8f5" : "#f1f8e9"
              }}>
                <Typography fontWeight={800} mb={1.5}
                  color={selectedBike?.due?.status === "DUE" ? "#e65100" : "#2e7d32"}
                  display="flex" alignItems="center" gap={0.5}>
                  <PaymentIcon fontSize="small" /> Payment Status
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Label>Status</Label>
                    <Box mt={0.3}>
                      <Chip
                        label={selectedBike?.due?.status || "PAID"} size="small"
                        icon={selectedBike?.due?.status === "DUE"
                          ? <WarningAmberIcon sx={{ fontSize: 13 }} />
                          : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                        sx={{
                          fontWeight: 700, fontSize: "0.72rem",
                          bgcolor: selectedBike?.due?.status === "DUE" ? "#fff3e0" : "#e8f5e9",
                          color:   selectedBike?.due?.status === "DUE" ? "#e65100" : "#2e7d32",
                          border: `1px solid ${selectedBike?.due?.status === "DUE" ? "#ffb74d" : "#a5d6a7"}`
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}><InfoBlock label="Total Due"   value={`₹${(selectedBike?.due?.totalAmount || 0).toLocaleString("en-IN")}`} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Paid So Far" value={`₹${(selectedBike?.due?.paidAmount || 0).toLocaleString("en-IN")}`} color="#2e7d32" /></Grid>
                  <Grid item xs={6}><InfoBlock label="Remaining"   value={`₹${(selectedBike?.due?.remainingAmount || 0).toLocaleString("en-IN")}`} color="#e65100" /></Grid>

                  {/* 📝 DUE NOTE */}
                  {selectedBike?.due?.note && (
                    <Grid item xs={12}>
                      <Label>Due Note</Label>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#555", mt: 0.1, whiteSpace: "pre-wrap" }}>
                        {selectedBike.due.note}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

 
                {selectedBike?.due?.payments?.length > 0 && (
                  <Box mt={2}>
                    <Label>Installment History</Label>
                    <Box mt={0.5} sx={{ maxHeight: 140, overflowY: "auto" }}>
                      {selectedBike.due.payments.map((p, i) => (
                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                          sx={{ py: 0.6, borderBottom: "1px dashed #e0e0e0" }}>
                          <Typography variant="caption" color="text.secondary">
                            {p.note || `Payment ${i + 1}`}
                          </Typography>
                          <Box textAlign="right">
                            <Typography variant="caption" fontWeight={700} color="#2e7d32">
                              ₹{(p.amount || 0).toLocaleString("en-IN")}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {p.date ? new Date(p.date).toLocaleDateString("en-IN") : ""}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* ── FINANCE ── */}
            {selectedBike?.finance?.companyName && (
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #bbdefb", bgcolor: "#e3f2fd" }}>
                  <Typography fontWeight={800} mb={1.5} color="#1565c0" display="flex" alignItems="center" gap={0.5}>
                    <AccountBalanceIcon fontSize="small" /> Finance Details
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={7}><InfoBlock label="Finance Company" value={selectedBike.finance.companyName} /></Grid>
                    <Grid item xs={5}><InfoBlock label="Finance Amount"  value={`₹${(selectedBike.finance.amount || 0).toLocaleString("en-IN")}`} color="#1565c0" /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* ── EXCHANGE ── */}
            {selectedBike?.exchange?.registrationNumber && (
              <Grid item xs={12} md={selectedBike?.finance?.companyName ? 6 : 12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #ffe0b2", bgcolor: "#fff3e0" }}>
                  <Typography fontWeight={800} mb={1.5} color="#e65100" display="flex" alignItems="center" gap={0.5}>
                    <SwapHorizIcon fontSize="small" /> Exchange Vehicle
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}> <InfoBlock label="Owner Name"      value={selectedBike.exchange.ownerName} /></Grid>
                    <Grid item xs={6}> <InfoBlock label="Phone"           value={selectedBike.exchange.phone} /></Grid>
                    <Grid item xs={6}> <InfoBlock label="Model"           value={selectedBike.exchange.model} /></Grid>
                    <Grid item xs={6}> <InfoBlock label="Location"        value={selectedBike.exchange.location} /></Grid>
                    <Grid item xs={12}><InfoBlock label="Registration No" value={selectedBike.exchange.registrationNumber} /></Grid>
                    <Grid item xs={6}> <InfoBlock label="Exchange Price"  value={`₹${(selectedBike.exchange.exchangePrice || 0).toLocaleString("en-IN")}`} color="#e65100" /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* ── CHARGES ── */}
            {(selectedBike?.charges?.rto || selectedBike?.charges?.insurance) ? (
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                  <Typography fontWeight={800} mb={1.5} color="#4527a0">💸 Charges</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><InfoBlock label="RTO"       value={`₹${(selectedBike?.charges?.rto || 0).toLocaleString("en-IN")}`} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Insurance" value={`₹${(selectedBike?.charges?.insurance || 0).toLocaleString("en-IN")}`} /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            ) : null}

            {/* ── EXPENSES ── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                <Typography fontWeight={800} mb={1.5} color="#00838f">🎁 Expenses / Freebies</Typography>
                <Grid container spacing={1}>
                  {[
                    // [label, value, isAmount]
                    ["Helmet",     selectedBike?.expenses?.helmet],
                    ["Seat Cover", selectedBike?.expenses?.seatCover],
                    ["Bike Cover", selectedBike?.expenses?.bikeCover],
                    ["Transport",  selectedBike?.expenses?.transport],
                    ["Petrol",     selectedBike?.expenses?.petrol],
                    ["Legguard",   selectedBike?.expenses?.legguard],
                    ["Broker",     selectedBike?.expenses?.broker],
                    ["TCR",        selectedBike?.expenses?.tcr],
                    ["Reg Broker", selectedBike?.expenses?.regBroker],
                    ["AMC / RSA",  selectedBike?.expenses?.amcRsa],
                    // 💼 NEW — other expenses
                    ["Other Expenses", selectedBike?.expenses?.otherExpenses, true],
                  ].filter(([, val]) => val != null && val !== 0).map(([label, val, isAmount]) => (
                    <Grid item xs={6} key={label}>
                      <InfoBlock
                        label={label}
                        value={isAmount ? inr(val) : String(val)}
                        color={isAmount ? "#00838f" : undefined}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            {/* ── GIFTS (array + total price) ── */}
            {giftItems.length > 0 && (
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #f8bbd0", bgcolor: "#fce4ec" }}>
                  <Typography fontWeight={800} mb={1.5} color="#ad1457">🎁 Gifts</Typography>

                  {giftItems.map((g, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                      sx={{ py: 0.5, borderBottom: "1px dashed #f48fb1" }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{g.name || "Unspecified"}</Typography>
                        {g.price != null && (
                          <Typography variant="caption" color="text.secondary">
                            {inr(g.price)} × {g.qty || 0}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Chip label={`Qty: ${g.qty ?? 0}`} size="small"
                          sx={{ bgcolor: "#f48fb1", color: "#880e4f", fontWeight: 700, fontSize: "0.65rem" }} />
                        {g.cost != null && (
                          <Typography variant="body2" fontWeight={700} color="#ad1457">
                            {inr(g.cost)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {/* 🎁 TOTAL */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
                    <Box>
                      <Label color="#ad1457">Total Gift Price</Label>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {giftItems.length} gift{giftItems.length > 1 ? "s" : ""} · {giftTotalQty} qty
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800} color="#ad1457">
                      {giftTotalPrice != null ? inr(giftTotalPrice) : "—"}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* ── SALESMAN ── */}
            {(selectedBike?.salesman?.name || selectedBike?.salesman?.incentive) && (
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                  <Typography fontWeight={800} mb={1.5} color="#795548" display="flex" alignItems="center" gap={0.5}>
                    <PersonIcon fontSize="small" /> Salesman
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={7}><InfoBlock label="Name"      value={selectedBike.salesman.name} /></Grid>
                    <Grid item xs={5}><InfoBlock label="Incentive" value={`₹${(selectedBike.salesman.incentive || 0).toLocaleString("en-IN")}`} color="#795548" /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* ── SOLD BY ── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                <Typography fontWeight={800} mb={1.5} color="#546e7a">👨‍💼 Sold By</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}><InfoBlock label="Name"  value={selectedBike?.soldBy?.name} /></Grid>
                  <Grid item xs={6}><InfoBlock label="Role"  value={selectedBike?.soldBy?.role} /></Grid>
                  <Grid item xs={12}><InfoBlock label="Email" value={selectedBike?.soldBy?.email} /></Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ── REMARKS ── */}
            {selectedBike?.remarks && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e7ef" }}>
                  <Typography fontWeight={800} mb={1} color="#546e7a">📝 Remarks</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedBike.remarks}</Typography>
                </Paper>
              </Grid>
            )}

          </Grid>
        ) : (
          <Alert severity="info">No data found</Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BikeDetailModal;